import { useState, useEffect, useRef } from "react";
import { categoryAttributeMapApi, attributeApi, productCategoryApi } from "../api/api";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination } from "../hooks/usePagination";
import Pagination        from "../components/Pagination";
import SearchableDropdown from "../components/SearchableDropdown";

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">Remove Mapping?</h3>
        <p className="rv-dialog-desc">
          <strong>{name}</strong> will be permanently removed. This action cannot be undone.
        </p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ── Map Modal ── */
function MapModal({ initial, onSave, onClose, saving, categories, attributes }) {
  const isEdit = !!initial?.mapID;
  const [form, setForm] = useState({
    categoryID:  initial?.categoryID  ?? "",
    attributeID: initial?.attributeID ?? "",
    isRequired:  initial?.isRequired  ?? false,
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.categoryID)  e.categoryID  = "Category is required.";
    if (!form.attributeID) e.attributeID = "Attribute is required.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...initial, ...form });
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form">
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><MapIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Mapping" : "New Mapping"}</h3>
            <p className="rv-modal-sub">
              {isEdit ? "Update the category–attribute mapping." : "Link an attribute to a product category."}
            </p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body">
          {/* Category */}
          <div className="rv-field">
            <label className="rv-label">Category <span className="rv-required">*</span></label>
            <SearchableDropdown
              options={categories}
              valueKey="categoryID"
              labelKey="categoryName"
              value={form.categoryID}
              onChange={val => {
                setForm(f => ({ ...f, categoryID: Number(val) }));
                setErrors(v => ({ ...v, categoryID: "" }));
              }}
              placeholder="Select category…"
              searchPlaceholder="Search categories…"
              error={errors.categoryID}
              pageSize={10}
            />
          </div>

          {/* Attribute */}
          <div className="rv-field">
            <label className="rv-label">Attribute <span className="rv-required">*</span></label>
            <SearchableDropdown
              options={attributes}
              valueKey="attributeID"
              labelKey="attributeName"
              value={form.attributeID}
              onChange={val => {
                setForm(f => ({ ...f, attributeID: Number(val) }));
                setErrors(v => ({ ...v, attributeID: "" }));
              }}
              placeholder="Select attribute…"
              searchPlaceholder="Search attributes…"
              error={errors.attributeID}
              pageSize={10}
            />
          </div>

          {/* Is Required toggle */}
          <div className="rv-field rv-field--row">
            <div>
              <label className="rv-label">Required</label>
              <p className="rv-field-hint">Whether this attribute is mandatory for the category</p>
            </div>
            <button
              type="button"
              className={`rv-toggle ${form.isRequired ? "rv-toggle--on" : ""}`}
              onClick={() => setForm(f => ({ ...f, isRequired: !f.isRequired }))}
            >
              <span className="rv-toggle-knob" />
            </button>
          </div>
        </div>

        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════ */
export default function CategoryAttributeMapPage() {
  const [maps,       setMaps]       = useState([]);
  const [categories, setCategories] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [loading,    setLoading]    = useState(true);

  const [search,       setSearch]       = useState("");
  const [filterCatID,  setFilterCatID]  = useState("all");
  const [filterReq,    setFilterReq]    = useState("all");

  const [modal,   setModal]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [toast,   setToast]   = useState(null);

  /* ── Resizable columns ── */
  const tableRef   = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  // Columns: #, Category, Attribute, Attr Type, Required, Actions
  useResizableColumns(tableRef, tableReady, [50, 200, 200, 130, 110, 100]);

  /* ── Fetch all ── */
  const fetchAll = async () => {
    try {
      setLoading(true);
      setTableReady(false);
      const [mapsData, catsData, attrsData] = await Promise.all([
        categoryAttributeMapApi.getAll(),
        productCategoryApi.getAll(),
        attributeApi.getAll(),
      ]);
      setMaps(Array.isArray(mapsData)  ? mapsData  : mapsData?.data  ?? []);
      setCategories(Array.isArray(catsData)  ? catsData  : catsData?.data  ?? []);
      setAttributes(Array.isArray(attrsData) ? attrsData : attrsData?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load data.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  /* ── Lookup helpers ── */
  const getCatName  = id => categories.find(c => c.categoryID  === id)?.categoryName  ?? `#${id}`;
  const getAttrName = id => attributes.find(a => a.attributeID === id)?.attributeName ?? `#${id}`;
  const getAttrType = id => attributes.find(a => a.attributeID === id)?.attributeType ?? "—";

  /* ── Save ── */
  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (formData.mapID) {
        await categoryAttributeMapApi.update(formData);
        showToast("Mapping updated successfully.");
      } else {
        await categoryAttributeMapApi.create(formData);
        showToast("Mapping created successfully.");
      }
      setModal(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await categoryAttributeMapApi.remove(confirm.id);
      showToast("Mapping removed.");
      setConfirm(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Unique categories in current data for filter dropdown ── */
  const catOptions = Array.from(
    new Map(maps.map(m => [m.categoryID, getCatName(m.categoryID)])).entries()
  );

  /* ── Filtered list ── */
  const filtered = maps.filter(m => {
    const catName  = getCatName(m.categoryID).toLowerCase();
    const attrName = getAttrName(m.attributeID).toLowerCase();
    const attrType = getAttrType(m.attributeID).toLowerCase();
    const q = search.toLowerCase();

    const matchSearch = !q || catName.includes(q) || attrName.includes(q) || attrType.includes(q);
    const matchCat    = filterCatID === "all" || m.categoryID === Number(filterCatID);
    const matchReq    =
      filterReq === "all" ||
      (filterReq === "required"    &&  m.isRequired) ||
      (filterReq === "not-required" && !m.isRequired);

    return matchSearch && matchCat && matchReq;
  });
  const {
  paged, page, totalPages, pageSize,
  setPage, setPageSize,
  pageStart, pageEnd, totalItems,
} = usePagination(filtered, 10);

  return (
    <div className="rv-map-page">

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      {confirm && (
        <ConfirmDialog
          name={confirm.name}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
      {modal && (
        <MapModal
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
          categories={categories}
          attributes={attributes}
        />
      )}

      {/* ── Topbar ── */}
      <div className="rv-map-topbar">
        <div className="rv-map-topbar-left">
          <h2 className="rv-map-heading">Category Attribute Map</h2>
          <span className="rv-map-count">{maps.length} total</span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
          <PlusIcon /> New Mapping
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="rv-map-filters">
        {/* Search */}
        <div className="rv-map-search">
          <SearchIcon />
          <input
            placeholder="Search by category, attribute or type…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-map-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>

        {/* Category filter dropdown */}
        <SearchableDropdown
          options={[
            { id: "all", name: "All Categories" },
            ...catOptions.map(([id, name]) => ({ id, name }))
          ]}
          valueKey="id"
          labelKey="name"
          value={filterCatID}
          onChange={val => { setFilterCatID(val); setPage(1); }}
          placeholder="All Categories"
          searchPlaceholder="Search categories…"
          className="rv-map-cat-filter"
          pageSize={10}
        />

        {/* Required filter tabs */}
        <div className="rv-filter-tabs">
          {[
            { value: "all",          label: "All" },
            { value: "required",     label: "Required" },
            { value: "not-required", label: "Optional" },
          ].map(s => (
            <button
              key={s.value}
              className={`rv-filter-tab ${filterReq === s.value ? "rv-filter-tab--active" : ""}`}
             onClick={() => { setFilterReq(s.value); setPage(1); }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table card ── */}
      <div className="rv-card rv-map-card">
        {loading ? (
          <div className="rv-map-empty">
            <div className="rv-loader" />
            <p>Loading mappings…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rv-map-empty">
            <div className="rv-empty-icon"><MapIcon /></div>
            <p className="rv-empty-title">No mappings found</p>
            <p className="rv-empty-sub">
              {search ? "Try a different search term." : "Create your first mapping to get started."}
            </p>
            {!search && (
              <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                <PlusIcon /> New Mapping
              </button>
            )}
          </div>
        ) : (
          <>
          <div className="rv-table-wrap rv-map-table-wrap">
            <table className="rv-table rv-map-table" ref={tableRef}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Category</th>
                  <th>Attribute</th>
                  <th>Attr Type</th>
                  <th>Required</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((m, i) => (
                  <tr key={m.mapID} className="rv-map-row">
                   <td className="rv-map-idx">{pageStart + i}</td>

                    {/* Category */}
                    <td>
                      <div className="rv-map-cat-cell">
                        <span className="rv-map-cat-name">{getCatName(m.categoryID)}</span>
                      </div>
                    </td>

                    {/* Attribute */}
                    <td>
                      <div className="rv-map-attr-cell">
                        <span className="rv-map-attr-name">{getAttrName(m.attributeID)}</span>
                      </div>
                    </td>

                    {/* Attr Type */}
                    <td>
                      <span className="rv-type-badge">{getAttrType(m.attributeID)}</span>
                    </td>

                    {/* Required */}
                    <td>
                      <span className={`rv-req-pill ${m.isRequired ? "rv-req-pill--yes" : "rv-req-pill--no"}`}>
                        {/* <span className="rv-status-dot" /> */}
                        {m.isRequired ? "Required" : "Optional"}
                      </span>
                    </td>

                    {/* Actions */}
                    <td>
                      <div className="rv-actions">
                        <button
                          className="rv-action-btn rv-action-btn--edit"
                          title="Edit"
                          onClick={() => setModal({ data: m })}
                        >
                          <EditIcon />
                        </button>
                        <button
                          className="rv-action-btn rv-action-btn--del"
                          title="Delete"
                          onClick={() =>
                            setConfirm({
                              id:   m.mapID,
                              name: `${getCatName(m.categoryID)} → ${getAttrName(m.attributeID)}`,
                            })
                          }
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
  page={page}
  totalPages={totalPages}
  pageSize={pageSize}
  setPage={setPage}
  setPageSize={setPageSize}
  pageStart={pageStart}
  pageEnd={pageEnd}
  totalItems={totalItems}
/>
          </>
        )}
      </div>

      {/* ── Resize hint ── */}
      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint">
          <GripIcon /> Drag column borders to resize
        </p>
      )}
    </div>
  );
}

/* ── Icons ── */
function PlusIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function EditIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function TrashIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>; }
function CloseIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function CheckIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>; }
function AlertIcon()   { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function ChevronIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>; }
function GripIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{width:13,height:13}}><circle cx="9" cy="5" r="1" fill="currentColor"/><circle cx="9" cy="12" r="1" fill="currentColor"/><circle cx="9" cy="19" r="1" fill="currentColor"/><circle cx="15" cy="5" r="1" fill="currentColor"/><circle cx="15" cy="12" r="1" fill="currentColor"/><circle cx="15" cy="19" r="1" fill="currentColor"/></svg>; }
function MapIcon()     { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>; }